import { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [wines, setWines] = useState([]);
  const [cart, setCart] = useState({}); // { slug: qty }
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [quickView, setQuickView] = useState(null);

  useEffect(() => {
    axios.get(`${API}/wines`).then((r) => setWines(r.data || [])).catch(() => {});
  }, []);

  const items = useMemo(
    () => wines.filter((w) => cart[w.slug]).map((w) => ({ ...w, qty: cart[w.slug] })),
    [wines, cart]
  );
  const total = items.reduce((s, it) => s + it.price_rsd * it.qty, 0);
  const cartCount = items.reduce((s, it) => s + it.qty, 0);

  const add = useCallback((slug, opts = {}) => {
    setCart((c) => ({ ...c, [slug]: (c[slug] || 0) + 1 }));
    if (opts.openDrawer === true) setDrawerOpen(true);
  }, []);
  const inc = useCallback((slug) => setCart((c) => ({ ...c, [slug]: (c[slug] || 0) + 1 })), []);
  const dec = useCallback(
    (slug) =>
      setCart((c) => {
        const next = (c[slug] || 0) - 1;
        if (next <= 0) {
          const { [slug]: _, ...rest } = c;
          return rest;
        }
        return { ...c, [slug]: next };
      }),
    []
  );
  const remove = useCallback(
    (slug) =>
      setCart((c) => {
        const { [slug]: _, ...rest } = c;
        return rest;
      }),
    []
  );
  const clear = useCallback(() => setCart({}), []);

  const placeOrder = useCallback(
    async (form, t) => {
      if (items.length === 0) return false;
      const payload = {
        ...form,
        items: items.map((it) => ({
          slug: it.slug,
          name: it.name,
          quantity: it.qty,
          price_rsd: it.price_rsd,
        })),
      };
      try {
        await axios.post(`${API}/orders`, payload);
        clear();
        setCheckoutOpen(false);
        setDrawerOpen(false);
        toast.success(t.shop.placed);
        return true;
      } catch (err) {
        toast.error(t.shop.error);
        return false;
      }
    },
    [items, clear]
  );

  const value = useMemo(
    () => ({
      wines,
      cart,
      items,
      total,
      cartCount,
      drawerOpen,
      setDrawerOpen,
      checkoutOpen,
      setCheckoutOpen,
      quickView,
      setQuickView,
      add,
      inc,
      dec,
      remove,
      clear,
      placeOrder,
    }),
    [
      wines,
      cart,
      items,
      total,
      cartCount,
      drawerOpen,
      checkoutOpen,
      quickView,
      add,
      inc,
      dec,
      remove,
      clear,
      placeOrder,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
